import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import {
    CitationValue,
    LiveCitationData,
    citationFieldLabel,
    erholCitationKind,
    formatNoteDate,
    latestNoteSourceSchema,
    noteSourceSchema,
    recentNoteStatisticsSourceSchema
} from "./liveCitationModel";
import styles from "./LiveCitation.module.css";

const SourceText = ({ text }: { text: string }) => (
    <div className={styles.text}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={{ img: ({ alt }) => <span>{alt}</span> }}>
            {text}
        </ReactMarkdown>
    </div>
);

const SourceValue = ({ value }: { value: CitationValue }) => {
    if (value === null || value === "") return <span>—</span>;
    if (typeof value === "string") return <SourceText text={value} />;
    if (typeof value !== "object") return <span>{String(value)}</span>;
    if (Array.isArray(value)) {
        return value.length ? (
            <ul className={styles.values}>
                {value.map((item, index) => (
                    <li key={index}>
                        <SourceValue value={item} />
                    </li>
                ))}
            </ul>
        ) : (
            <span>—</span>
        );
    }
    return (
        <dl className={styles.fields}>
            {Object.entries(value).map(([key, item]) => (
                <div key={key}>
                    <dt>{citationFieldLabel(key)}</dt>
                    <dd>
                        <SourceValue value={item} />
                    </dd>
                </div>
            ))}
        </dl>
    );
};

export const LiveCitation = ({ citation }: { citation: LiveCitationData }) => {
    const { t, i18n } = useTranslation();
    const erholKind = erholCitationKind(citation.source);
    const title = erholKind ? t(`liveCitation.sources.${erholKind}`) : citation.title;
    const latestNote = erholKind === "latestNote" ? latestNoteSourceSchema.safeParse(citation.value) : undefined;
    const latestNoteDate = latestNote?.success ? formatNoteDate(latestNote.data.note?.timestamp, i18n.language) : undefined;
    const recentStatistics = erholKind === "workflowStatistics"
        ? recentNoteStatisticsSourceSchema.safeParse(citation.value)
        : undefined;
    const notes = noteSourceSchema.safeParse(citation.value);
    return (
        <section className={styles.source}>
            <h3 className={styles.title}>{title}</h3>
            {latestNote?.success ? (
                <>
                    <p className={styles.client}>{latestNote.data.project_name}</p>
                    {latestNote.data.note ? (
                        <article className={styles.note}>
                            <dl className={styles.fields}>
                                {latestNote.data.note.title && (
                                    <div><dt>{t("liveCitation.noteTitle")}</dt><dd>{latestNote.data.note.title}</dd></div>
                                )}
                                {latestNoteDate && (
                                    <div><dt>{t("liveCitation.date")}</dt><dd>{latestNoteDate}</dd></div>
                                )}
                                {(latestNote.data.note.is_struck || typeof latestNote.data.note.is_signed === "boolean") && (
                                    <div>
                                        <dt>{t("liveCitation.status")}</dt>
                                        <dd>{t(latestNote.data.note.is_struck
                                            ? "liveCitation.struck"
                                            : latestNote.data.note.is_signed ? "liveCitation.signed" : "liveCitation.unsigned")}</dd>
                                    </div>
                                )}
                                <div>
                                    <dt>{t("liveCitation.workflowTypes")}</dt>
                                    <dd>
                                        {latestNote.data.note.workflow_labels?.length
                                            ? latestNote.data.note.workflow_labels.join(" · ")
                                            : t("liveCitation.noWorkflowType")}
                                    </dd>
                                </div>
                            </dl>
                        </article>
                    ) : <p>{t("liveCitation.noNotes")}</p>}
                </>
            ) : recentStatistics?.success ? (
                <>
                    <p className={styles.client}>{recentStatistics.data.project_name}</p>
                    <p className={styles.scope}>
                        {t("liveCitation.recentNotesScope", {
                            requested: recentStatistics.data.requested_note_count,
                            visible: recentStatistics.data.visible_note_count
                        })}
                    </p>
                    <dl className={styles.fields}>
                        <div>
                            <dt>{t("liveCitation.workflowInstanceCount")}</dt>
                            <dd>{recentStatistics.data.workflow_instance_count}</dd>
                        </div>
                        <div>
                            <dt>{t("liveCitation.unclassifiedNoteCount")}</dt>
                            <dd>{recentStatistics.data.unclassified_note_count}</dd>
                        </div>
                    </dl>
                    {recentStatistics.data.rows.length ? (
                        <table className={styles.statisticsTable}>
                            <thead><tr><th>{t("liveCitation.workflowTypes")}</th><th>{t("liveCitation.count")}</th></tr></thead>
                            <tbody>
                                {recentStatistics.data.rows.map(row => (
                                    <tr key={row.label}><td>{row.label}</td><td>{row.count}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p>{t("liveCitation.noWorkflowType")}</p>}
                </>
            ) : notes.success ? (
                <>
                    <p className={styles.client}>{notes.data.client}</p>
                    {notes.data.showing && <p className={styles.scope}>{notes.data.showing}</p>}
                    {notes.data.signed_notes.map((note, index) => (
                        <article className={styles.note} key={index}>
                            <dl className={styles.metadata}>
                                <div>
                                    <dt>{t("liveCitation.date")}</dt>
                                    <dd>{note.date || "—"}</dd>
                                </div>
                                <div>
                                    <dt>{t("liveCitation.author")}</dt>
                                    <dd>{note.author || "—"}</dd>
                                </div>
                            </dl>
                            <SourceText text={note.text} />
                        </article>
                    ))}
                </>
            ) : (
                <SourceValue value={citation.value} />
            )}
        </section>
    );
};
